// Contrato de retail-promotions-business derivado de los tipos generados de su openapi.
import type {
    components,
    operations,
} from '@/api/services/retail-promotions/retail-promotions.gen';

type Schemas = components['schemas'];
type QueryOf<K extends keyof operations> = NonNullable<operations[K]['parameters']['query']>;

export type ProductCategory = Schemas['ProductCategoryEnum'];
export type ProductTier = Schemas['ProductTierEnum'];

export type SalePromotionStatus = Schemas['SalePromotionStatusEnum'];
export type SalePromotionPhase = Schemas['SalePromotionPhaseEnum'];
export type SalePromotionTransition = Schemas['SalePromotionTransitionEnum'];
export type SalePromotionLogEvent = Schemas['SalePromotionLogEventEnum'];
export type SalePromotionLogFormat = NonNullable<QueryOf<'publicListSalePromotionLog'>['format']>;
export type PromotionAppliesTo = Schemas['PromotionAppliesToEnum'];
export type PromotionPaymentOption = Schemas['PromotionPaymentOptionEnum'];
export type DiscountType = Schemas['DiscountTypeEnum'];
export type GiftMode = Schemas['GiftModeEnum'];
export type CouponScope = Schemas['CouponScopeEnum'];

export type DiscountCodeOrigin = Schemas['DiscountCodeOriginEnum'];
type VisibleCodeStatus = Schemas['DiscountCode']['status'];
/** Estado de un código de Admin VL. */
export type DiscountCodeStatus = Exclude<VisibleCodeStatus, 'USED'>;
/** Estado de un cupón emitido por una venta. */
export type CouponStatus = Schemas['CouponStatusEnum'];
export type DiscountCodeTransition = Schemas['DiscountCodeTransitionEnum'];

export type RetailPromotionsErrorCode = Schemas['RetailPromotionsErrorCodeEnum'];

export type SalePromotionKind = Schemas['SalePromotionKindEnum'];
export type ReferralChannel = Schemas['ReferralPromotionChannelEnum'];
export type ReferralTrigger = Schemas['ReferralTriggerEnum'];
export type ReferralRewardType = Schemas['ReferralRewardTypeEnum'];

export type AccessorySubcategory = Schemas['AccessorySubcategoryEnum'];
/** Valores de `AccessorySubcategoryEnum` en runtime; el tipo de abajo exige que estén todos. */
export const ACCESSORY_SUBCATEGORIES = [
    'AUDIO',
    'CHARGING',
    'PROTECTION',
    'CABLES',
    'WEARABLES',
    'OTHER',
] as const satisfies readonly AccessorySubcategory[];
type MissingAccessorySubcategory = Exclude<
    AccessorySubcategory,
    (typeof ACCESSORY_SUBCATEGORIES)[number]
>;
const allAccessorySubcategoriesListed: [MissingAccessorySubcategory] extends [never]
    ? true
    : MissingAccessorySubcategory = true;
void allAccessorySubcategoriesListed;

// Bodies de crear y editar.

export type DiscountValueInput = Schemas['DiscountValueInput'];
export type GiftLeverInput = Schemas['GiftLeverInput'];
export type SalePromotionLeversInput = Schemas['SalePromotionLeversInput'];
/**
 * `costCenterId` entra por intersección: el `.gen.ts` commiteado es anterior a que el lambda lo
 * aceptara. Centro de costo de lo que la promo paga a la wallet; al editar, `null` lo quita.
 */
type WithCostCenter = { costCenterId?: string | null };
export type CreateSalePromotionRequest = Schemas['CreateSalePromotionRequest'] & WithCostCenter;
export type UpdateSalePromotionRequest = Schemas['UpdateSalePromotionRequest'] & WithCostCenter;
export type ChangeSalePromotionStatusRequest = Schemas['ChangeSalePromotionStatusRequest'];
export type CreateDiscountCodeRequest = Schemas['CreateDiscountCodeRequest'];
export type ChangeDiscountCodeStatusRequest = Schemas['ChangeDiscountCodeStatusRequest'];
export type ReferralPromotionInput = Schemas['ReferralPromotionInput'];
export type ReferralRewardInput = Schemas['ReferralRewardInput'];
export type CouponLeverInput = Schemas['CouponLeverInput'];

export type ListSalePromotionsQuery = QueryOf<'publicListSalePromotions'>;
export type CreateSalePromotionQuery = QueryOf<'publicCreateSalePromotion'>;
export type UpdateSalePromotionQuery = QueryOf<'publicUpdateSalePromotion'>;
export type ChangeSalePromotionStatusQuery = QueryOf<'publicChangeSalePromotionStatus'>;
export type ListSalePromotionLogQuery = QueryOf<'publicListSalePromotionLog'>;
export type ListDiscountCodesQuery = QueryOf<'publicListDiscountCodes'>;

// Respuestas: el `data` de cada `ApiResponse`.

export type DiscountValue = Schemas['DiscountValue'];
export type AccessoryDiscountLever = Schemas['AccessoryDiscountLever'];
export type GiftLever = Schemas['GiftLever'];
export type CouponLever = Schemas['CouponLever'];
export type AirtimeLever = Schemas['AirtimeLever'];
export type ExtendedWarrantyLever = Schemas['ExtendedWarrantyLever'];
export type WalletBonusLever = Schemas['WalletBonusLever'];
export type SalePromotionLevers = Schemas['SalePromotionLevers'];
export type SalePromotionScope = Schemas['SalePromotionScope'];
export type SalePromotionSegmentation = Schemas['SalePromotionSegmentation'];

/** `costCenterId` ausente = sin asignar; ver `WithCostCenter`. */
export type SalePromotion = Schemas['SalePromotion'] & { costCenterId?: string };
export type ReferralPromotionConfig = Schemas['ReferralPromotionConfig'];
export type ReferralReward = Schemas['ReferralReward'];
export type SalePromotionListItem = Schemas['SalePromotionListItem'] & { costCenterId?: string };
export type SalePromotionKpis = Schemas['SalePromotionKpis'];
export type ListSalePromotionsResponse = Schemas['ListSalePromotionsResponse'];

export type CreateSalePromotionResponse = Schemas['CreateSalePromotionApiResponse']['data'];
export type GetSalePromotionResponse = Schemas['GetSalePromotionApiResponse']['data'];
export type UpdateSalePromotionResponse = Schemas['UpdateSalePromotionApiResponse']['data'];
export type ChangeSalePromotionStatusResponse =
    Schemas['ChangeSalePromotionStatusApiResponse']['data'];

export type SalePromotionLogEntry = Schemas['SalePromotionLogEntry'];
export type ListSalePromotionLogResponse = Schemas['ListSalePromotionLogResponse'];

/** Los campos de cupón (`ownerCustomerId`, `issuedBySaleId`, `couponScope`) son `null` en los de Admin. */
export type DiscountCode = Schemas['DiscountCode'];
export type ListDiscountCodesResponse = Schemas['ListDiscountCodesResponse'];
export type CreateDiscountCodeResponse = Schemas['CreateDiscountCodeApiResponse']['data'];
export type ChangeDiscountCodeStatusResponse =
    Schemas['ChangeDiscountCodeStatusApiResponse']['data'];

// Segmentos que el Admin VL puede elegir en una promo.

export type SalePromotionSegment = Schemas['SalePromotionSegmentItem'];
export type ListSalePromotionSegmentsResponse = Schemas['SalePromotionSegmentsResponse'];
